/**
 * Service Provider — Central place to get service instances.
 *
 * In dev mode (VITE_DEV_MODE=true), returns mock implementations.
 * In production, returns real Supabase/Fly/Stripe-backed implementations.
 *
 * Usage in components:
 *   import { services } from "../services/index.ts";
 *   const user = await services.auth.getCurrentUser();
 */

import type {
  AuthService,
  CreditsService,
  BillingService,
  GatewayService,
  ChannelsService,
  SessionsService,
  AutomationsService,
  DevicesService,
  SubAgentsService,
  IntegrationsService,
} from "./types.ts";

export type {
  AuthService,
  CreditsService,
  BillingService,
  GatewayService,
  ChannelsService,
  SessionsService,
  AutomationsService,
  DevicesService,
  SubAgentsService,
  IntegrationsService,
} from "./types.ts";
export type {
  UserProfile,
  CreditBalance,
  UsageEntry,
  GatewayConnectionState,
  ChatMessage,
  ConversationMode,
  ChannelInfo,
  ChannelStatus,
  ChannelSetupStep,
  ChannelSetupConfig,
  ChannelSetupVariant,
  ValidateCredentialsResult,
  TestChannelResult,
  SessionInfo,
  SessionSearchResult,
  AutomationInfo,
  AutomationAction,
  CreateAutomationInput,
  AutomationRun,
  DeviceInfo,
  IntegrationInfo,
  IntegrationStatus,
  IntegrationType,
  OAuthConnectResult,
  ToolActivityEvent,
  SubAgentRun,
  SubAgentRunStatus,
  SubAgentEvent,
  CanvasEvent,
  CanvasEventType,
  SubscriptionInfo,
  SubscriptionPlan,
  CreditPackId,
  CreditPack,
  TtsAudioMeta,
} from "./types.ts";
export { CREDIT_PACKS, PLAN_LABELS } from "./types.ts";

export type Services = {
  auth: AuthService;
  credits: CreditsService;
  billing: BillingService;
  gateway: GatewayService;
  channels: ChannelsService;
  sessions: SessionsService;
  automations: AutomationsService;
  devices: DevicesService;
  subAgents: SubAgentsService;
  integrations: IntegrationsService;
};

let _services: Services | null = null;

export function getServices(): Services {
  if (_services) return _services;
  throw new Error("Services not initialized. Call initServices() first.");
}

export async function initServices(): Promise<Services> {
  if (_services) return _services;

  const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

  if (isDevMode) {
    _services = await initMockServices();
    console.log(
      "%c[Katanoid] Dev mode active — using mock services",
      "color: #30d158; font-weight: bold;"
    );
    console.log(
      "%cTip: Run %ckatanoid.resetCredits()%c in console to reset credit balance",
      "color: #86868b;",
      "color: #0a84ff; font-family: monospace;",
      "color: #86868b;"
    );
  } else {
    _services = await initRealServices();
  }

  return _services;
}

// ── Mock services (dev mode) ──

async function initMockServices(): Promise<Services> {
  const { MockAuthService } = await import("./mock-auth.ts");
  const { MockCreditsService } = await import("./mock-credits.ts");
  const { MockGatewayService } = await import("./mock-gateway.ts");
  const { MockChannelsService } = await import("./mock-channels.ts");
  const { MockSessionsService } = await import("./mock-sessions.ts");
  const { MockAutomationsService } = await import("./mock-automations.ts");
  const { MockDevicesService } = await import("./mock-devices.ts");
  const { MockBillingService } = await import("./mock-billing.ts");
  const { MockSubAgentsService } = await import("./mock-sub-agents.ts");
  const { MockIntegrationsService } = await import("./mock-integrations.ts");

  const auth = new MockAuthService();
  const credits = new MockCreditsService();
  const billing = new MockBillingService();
  const gateway = new MockGatewayService();
  const channels = new MockChannelsService();
  const sessions = new MockSessionsService();
  const automations = new MockAutomationsService();
  const devices = new MockDevicesService();
  const subAgents = new MockSubAgentsService();
  const integrations = new MockIntegrationsService();

  // Wire up: when gateway deducts credits, update the mock credits service
  gateway.setCreditDeductCallback((amount) => {
    credits.deduct(amount, "chat");
  });

  // Expose helpers on window for dev console
  (window as unknown as Record<string, unknown>).katanoid = {
    resetCredits: () => {
      credits.resetBalance();
      console.log("Credits reset to default");
    },
    setPlan: (plan: string) => {
      billing.setPlan(plan as "free" | "pro" | "pro_plus");
    },
    signOut: () => auth.signOut(),
    services: { auth, credits, billing, gateway, channels, sessions, automations, devices, subAgents, integrations },
  };

  return { auth, credits, billing, gateway, channels, sessions, automations, devices, subAgents, integrations };
}

// ── Real services (production) ──

async function initRealServices(): Promise<Services> {
  const { RealAuthService } = await import("./real-auth.ts");
  const { RealCreditsService } = await import("./real-credits.ts");
  const { RealSessionsService } = await import("./real-sessions.ts");
  const { RealGatewayService } = await import("./real-gateway.ts");
  const { RealChannelsService } = await import("./real-channels.ts");
  const { RealAutomationsService } = await import("./real-automations.ts");
  const { RealBillingService } = await import("./real-billing.ts");

  const { RealDevicesService } = await import("./real-devices.ts");
  const { RealSubAgentsService } = await import("./real-sub-agents.ts");
  const { RealIntegrationsService } = await import("./real-integrations.ts");

  const auth = new RealAuthService();
  const credits = new RealCreditsService();
  const billing = new RealBillingService();
  const gateway = new RealGatewayService();
  const channels = new RealChannelsService();
  const sessions = new RealSessionsService();
  const automations = new RealAutomationsService();
  const devices = new RealDevicesService();
  const subAgents = new RealSubAgentsService();
  const integrations = new RealIntegrationsService();

  console.log(
    "%c[Katanoid] Production mode — all core services active (Supabase)",
    "color: #0a84ff; font-weight: bold;"
  );

  return { auth, credits, billing, gateway, channels, sessions, automations, devices, subAgents, integrations };
}
